import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Link } from '@setvi/shared/interfaces';
import {
  createPreviewTest,
  getPreviewResults,
  normalizePreviewResults,
  DEFAULT_EMAIL_CLIENTS,
  DEFAULT_CLIENT_META
} from '../../EmailPreview';
import type {
  CreatePreviewTestResponse,
  PreviewTestResultResponse
} from '../../EmailPreview/types';

export enum EmailPreviewQueryKey {
  PREVIEW_CREATE = 'email-preview-create',
  PREVIEW_RESULTS = 'email-preview-results'
}

interface UsePreviewResultsOptions {
  testId: string | null;
  expectedClientCount?: number;
  onComplete?: (results: PreviewTestResultResponse) => void;
}

export const usePreviewResults = ({
  testId,
  expectedClientCount = DEFAULT_EMAIL_CLIENTS.length,
  onComplete
}: UsePreviewResultsOptions) => {
  const query = useQuery({
    queryKey: [EmailPreviewQueryKey.PREVIEW_RESULTS, testId],
    queryFn: async () => {
      if (!testId) throw new Error('No test ID');
      const rawResults = await getPreviewResults(testId);
      return normalizePreviewResults(testId, rawResults, expectedClientCount);
    },
    enabled: Boolean(testId),
    retry: 2,
    refetchInterval: (data, queryInfo) => {
      // Stop polling if there's an error or fetch failures
      if (queryInfo.state.error || queryInfo.state.fetchFailureCount > 0) {
        return false;
      }
      if (!data || data.status === 'processing') {
        return 2000;
      }
      return false;
    },
    refetchIntervalInBackground: false,
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 10,
    onSuccess: data => {
      if (data.status !== 'processing' && onComplete) {
        onComplete(data);
      }
    }
  });

  return query;
};

interface CreatePreviewParams {
  subject: string;
  html: string;
}

export const useCreatePreviewTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subject, html }: CreatePreviewParams) =>
      createPreviewTest(subject, html),
    onSuccess: (data: CreatePreviewTestResponse) => {
      queryClient.invalidateQueries([
        EmailPreviewQueryKey.PREVIEW_RESULTS,
        data.id
      ]);
    }
  });
};

interface UseCachedCreatePreviewOptions {
  subject: string;
  html: string;
  contentHash: string | null;
  enabled?: boolean;
  links?: Link[];
}

export const useCachedCreatePreview = ({
  subject,
  html,
  contentHash,
  enabled = true,
  links
}: UseCachedCreatePreviewOptions) =>
  useQuery({
    queryKey: [EmailPreviewQueryKey.PREVIEW_CREATE, contentHash],
    queryFn: () => createPreviewTest(subject, html, links),
    enabled: enabled && Boolean(contentHash),
    staleTime: 1000 * 60 * 30,
    cacheTime: 1000 * 60 * 30
  });

export { DEFAULT_EMAIL_CLIENTS, DEFAULT_CLIENT_META };
export type { PreviewTestResultResponse };
